/**
 * Cards page — the Card Wall (§13.1 signature, §16 Phase 1).
 *
 * Phase 1 AC: "add/edit/archive cards from a fresh, empty DB".
 */

import { motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, CreditCard, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { HeadroomBar, trimRate } from "@/components/caps/HeadroomBar";

import { CardObject, CardObjectSkeleton, cardLabel } from "@/components/cards/CardObject";
import { CardFormDialog } from "@/components/cards/CardFormDialog";
import { stagger, Tilt } from "@/components/ui/motion";
import { Button, EmptyState, ErrorState, PageHeader, Panel } from "@/components/ui/primitives";
import { capsApi, cardsApi, type Card, type CardRewards } from "@/lib/api/client";
import { cn } from "@/lib/cn";

export function CardsPage() {
  const qc = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [editing, setEditing] = useState<Card | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cards", showClosed],
    queryFn: () => cardsApi.list(showClosed),
  });

  // One call for the whole wall's headroom (§13.3 "slim cap-headroom microbar").
  const { data: rewards } = useQuery({ queryKey: ["caps", "wallet"], queryFn: capsApi.wallet });
  const rewardsById = new Map((rewards ?? []).map((r) => [r.card_id, r]));

  const closeMutation = useMutation({
    mutationFn: (card: Card) =>
      card.status === "active" ? cardsApi.close(card.id) : cardsApi.reopen(card.id),
    onSuccess: (card) => {
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["info"] });
      toast.success(
        card.status === "closed"
          ? `${cardLabel(card)} closed. Its history is kept.`
          : `${cardLabel(card)} reopened.`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cards = data ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={data ? `${cards.length} card${cards.length === 1 ? "" : "s"}${showClosed ? " · including closed" : ""}` : "Your wallet"}
        title="Cards"
        description="Every card you hold. Add them here, or let a statement discover them. Click a card to edit it."
        actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowClosed((v) => !v)}
            aria-pressed={showClosed}
          >
            {showClosed ? "Hide closed" : "Show closed"}
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            Add card
          </Button>
        </>
        }
      />

      {isError ? (
        <Panel>
          <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
        </Panel>
      ) : isLoading ? (
        <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <CardObjectSkeleton key={i} />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <Panel>
          <EmptyState
            icon={CreditCard}
            title="No cards yet"
            body={
              showClosed
                ? "You haven't closed any cards, and haven't added any either."
                : "Add your first card to start tracking it. You'll need its issuer and last 4 digits — nothing else is required."
            }
            action={
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                Add card
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {cards.map((card, i) => (
            <div
              key={card.id}
              className="animate-rise space-y-3"
              // The one orchestrated load stagger (§13.1). Capped so a wall of
              // 50 cards doesn't take three seconds to finish arriving.
              style={stagger(i, 80, 55, 8)}
            >
              <motion.div layoutId={`card-${card.id}`} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
                <Tilt>
                  <CardObject card={card} onClick={() => setEditing(card)} />
                </Tilt>
              </motion.div>
              <div className="space-y-1 px-1">
                {card.status === "active" && (
                  <RewardsLine cardId={card.id} rewards={rewardsById.get(card.id)} />
                )}
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to={`/cards/${card.id}`}
                    className="inline-flex items-center gap-1 text-xs text-muted transition-colors hover:text-ink"
                  >
                    Details <ArrowUpRight className="h-3 w-3" aria-hidden />
                  </Link>
                  <span className="flex items-center gap-1">
                    <span className="font-mono text-[10.5px] text-faint">
                      {card.has_pdf_password ? "PDF password saved" : "No PDF password"}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => closeMutation.mutate(card)}
                      disabled={closeMutation.isPending}
                    >
                      {card.status === "active" ? "Close" : "Reopen"}
                    </Button>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <CardFormDialog
          card={editing}
          open
          onOpenChange={(open) => {
            if (!open) {
              setCreating(false);
              setEditing(null);
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Under each card: the capped rules' headroom, or the learned rate when there
 * are no rules. Estimates, drawn on the dashed provisional track (§2.7).
 */
function RewardsLine({ cardId, rewards }: { cardId: string; rewards: CardRewards | undefined }) {
  if (!rewards) return null;
  const capped = rewards.in_force.filter((s) => s.cap_amount !== null).slice(0, 2);
  return (
    <Link
      to={`/cards/${cardId}?tab=rewards`}
      className="-mx-1 block space-y-1.5 rounded-lg px-1 py-1 transition-colors hover:bg-raised/50"
      title="Open this card's rewards"
    >
      {rewards.basis === "rules" && capped.length > 0 ? (
        capped.map((s) => <HeadroomBar key={s.rule_id} status={s} compact />)
      ) : (
        <span
          className={cn("text-xs", rewards.learned.drop_warning ? "text-warn" : "text-faint")}
          title={rewards.learned.drop_warning ?? undefined}
        >
          {rewards.basis === "rules"
            ? "Rules entered · no caps"
            : rewards.learned.rate_percent !== null
              ? rewards.learned.drop_warning && rewards.learned.usual_rate_percent
                ? `Pays ${trimRate(rewards.learned.rate_percent)}% now · was ${trimRate(rewards.learned.usual_rate_percent)}%`
                : `Pays ${trimRate(rewards.learned.rate_percent)}% · learned from statements`
              : "No cashback history yet"}
        </span>
      )}
    </Link>
  );
}
